import { useState, useEffect, useCallback } from "react";
import { getUserId } from "../utils/Auth";
import API_BASE_URL from "../config";

const useOrdersData = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const userId = getUserId();

  const fetchOrders = useCallback(async (year = null, company = "All") => {
    if (!userId) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      const query = `?year=${year}&company=${company}`;

      const response = await fetch(
        `${API_BASE_URL}/api/orders/my-orders${query}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error("⚠️ Hiba a rendelési adatok betöltésekor.");
      }

      const data = await response.json();

      // 🔹 Invoice fájlnév betöltése a backendből
      const processedOrders = data.map((order) => ({
        ...order,
        invoiceFileName:
          order.invoiceFileName ||
          (order.invoiceUrl ? order.invoiceUrl.split("/").pop() : null),
        grossProfit: order.grossProfit ?? 0,
        profitMargin: order.profitMargin ?? 0,
        totalCost: order.totalCost ?? 0,
      }));

      setOrders(processedOrders);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // useEffect(() => {
  //   fetchOrders();
  // }, [fetchOrders]); // ✅ Dependency array-be a `fetchOrders` kerül

  const fetchAllOrders = useCallback(async (year = null, company = "All") => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const query = `?year=${year}&company=${company}`;

      const response = await fetch(
        `${API_BASE_URL}/api/orders/all-orders${query}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        });

      if (!response.ok) {
        throw new Error("⚠️ Hiba az összes rendelés betöltésekor.");
      }

      const data = await response.json();

      setOrders(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteOrder = async (orderId, isAdminView = false, year = null, company = "All") => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/orders/${orderId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Hiba történt a rendelés törlésekor.");

      if (isAdminView) {
        await fetchAllOrders(year, company);
      } else {
        await fetchOrders(year, company);
      }
    } catch (error) {
      console.error("❌ Hiba a rendelés törlésekor:", error);
    }
  };

  const updateOrder = async (updatedOrder, isAdminView = false, year = null, company = "All") => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/orders/${updatedOrder.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            street: updatedOrder.street,
            customerSource: updatedOrder.customerSource ?? 0,
            orderItems: updatedOrder.orderItems.map((item) => ({
              productId: item.productId,
              quantityMP: Number(item.quantityMP),
              quantityML: Number(item.quantityML),
              quantityBUC: !item.quantityBUC ? 0 : Math.floor(Number(item.quantityBUC)),
              length: Number(item.length),
              width: Number(item.width),
              quantity: Number(item.quantity),
              bizot: item.bizot,
            })),
            discountPercentage: updatedOrder.discountPercentage,
            preferentialPrice: updatedOrder.preferentialPrice,
          }),
        }
      );
      if (!response.ok) {
        const text = await response.text();
        console.error("❌ Backend response:", text);
        return false;
      }

      if (isAdminView) {
        await fetchAllOrders(year, company);
      } else {
        await fetchOrders(year, company);
      }

      return true;
    } catch (error) {
      console.error("❌ Hiba a rendelés frissítésénél:", error);
      return false;
    }
  };

  // const openDrawingModal = async (order) => {
  //   try {
  //     const token = localStorage.getItem("token");
  //     const response = await fetch(`${API_BASE_URL}/api/orders/${order.id}`, {
  //       method: "GET",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `Bearer ${token}`,
  //       },
  //     });

  //     if (!response.ok) {
  //       throw new Error(`Hiba a rendelés ${order.id} adatainak lekérésekor.`);
  //     }

  //     const updatedOrder = await response.json();
  //     return updatedOrder.drawingData || "N/A";
  //   } catch (error) {
  //     console.error("❌ Hiba a rendelés adatainak lekérésekor:", error);
  //     return "N/A";
  //   }
  // };

  // const handleSaveDrawing = async (order, signatureRef) => {
  //   if (!order) return false;

  //   let updatedDrawing = "N/A";
  //   if (signatureRef.current && !signatureRef.current.isEmpty()) {
  //     updatedDrawing = signatureRef.current.toDataURL("image/png");
  //   }

  //   try {
  //     const token = localStorage.getItem("token");
  //     const response = await fetch(
  //       `${API_BASE_URL}/api/orders/${order.id}/drawing`,
  //       {
  //         method: "PATCH",
  //         headers: {
  //           "Content-Type": "application/json",
  //           Authorization: `Bearer ${token}`,
  //         },
  //         body: JSON.stringify({ drawingData: updatedDrawing }),
  //       }
  //     );

  //     if (!response.ok) {
  //       throw new Error("Hiba a rajz mentésekor.");
  //     }

  //     await fetchOrders();
  //     return true;
  //   } catch (error) {
  //     console.error("❌ Hiba a rajz mentésekor:", error);
  //     return false;
  //   }
  // };

  const uploadDrawing = async (orderId, file) => {
    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("drawing", file);

    const response = await fetch(`${API_BASE_URL}/api/orders/${orderId}/drawing`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) throw new Error("Hiba a rajz feltöltésekor.");
  };

  const downloadDrawing = (drawingUrl) => {
    window.open(drawingUrl, "_blank");
  };

  const deleteDrawing = async (orderId) => {
    const token = localStorage.getItem("token");

    const response = await fetch(`${API_BASE_URL}/api/orders/${orderId}/drawing`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) throw new Error("Hiba a rajz törlésekor.");
  };


  // const handleClearDrawing = async (order, signatureRef) => {
  //   if (!order) return false;

  //   try {
  //     const token = localStorage.getItem("token");
  //     const response = await fetch(
  //       `${API_BASE_URL}/api/orders/${order.id}/drawing`,
  //       {
  //         method: "PATCH",
  //         headers: {
  //           "Content-Type": "application/json",
  //           Authorization: `Bearer ${token}`,
  //         },
  //         body: JSON.stringify({ drawingData: "N/A" }),
  //       }
  //     );

  //     if (!response.ok) {
  //       throw new Error("Hiba a rajz törlésekor.");
  //     }

  //     if (signatureRef.current) {
  //       signatureRef.current.clear();
  //     }

  //     await fetchOrders();
  //     return true;
  //   } catch (error) {
  //     console.error("❌ Hiba a rajz törlésekor:", error);
  //     return false;
  //   }
  // };

  const uploadInvoice = async (orderId, file) => {
    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("invoice", file);

    const response = await fetch(
      `${API_BASE_URL}/api/orders/${orderId}/invoice`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      }
    );

    if (!response.ok) throw new Error("Hiba a számla feltöltésekor.");
  };

  const downloadInvoice = (invoiceUrl) => {
    window.open(invoiceUrl, "_blank");
  };

  const deleteInvoice = async (orderId) => {
    const token = localStorage.getItem("token");

    const response = await fetch(
      `${API_BASE_URL}/api/orders/${orderId}/invoice`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (!response.ok) throw new Error("Hiba a számla törlésekor.");
  };

  const toggleFinalizatStatus = async (orderId, isAdminView = false, year = null, company = "All") => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE_URL}/api/orders/${orderId}/toggle-finalizat`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Eroare la schimbarea statusului comenzii.");
      }

      if (isAdminView) {
        await fetchAllOrders(year, company);
      } else {
        await fetchOrders(year, company);
      }
    } catch (error) {
      console.error("❌ Eroare la schimbarea statusului:", error);
    }
  };

  return {
    orders,
    setOrders,
    fetchOrders,
    fetchAllOrders,
    loading,
    error,
    uploadInvoice,
    downloadInvoice,
    deleteInvoice,
    deleteOrder,
    updateOrder,
    toggleFinalizatStatus,
    // openDrawingModal,
    // handleSaveDrawing,
    // handleClearDrawing,
    uploadDrawing,
    downloadDrawing,
    deleteDrawing,
  };
};

export default useOrdersData;
